import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/shared/ui/button';
import { CartButton } from '@/features/user/components/atoms';
import { ShoppingCart } from '@/features/user/pages/Checkout/components/organisms/ShoppingCart';
import { SearchOverlay } from '../SearchOverlay';
import { Menu, ShoppingBag, Search, User, Heart, LogOut, Settings, X, Globe, Mail, Share2 } from 'lucide-react';
import { MegaMenu } from '../MegaMenu';
import { useUIStore } from '@/stores/useUIStore';
import { useSession } from '@/features/shared/auth/hooks/useSession';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shared/ui/dropdown-menu';
import { useCartStore } from '@/features/user/stores/useCartStore';

import { CustomAvatar } from '@/components/shared/ui/CustomAvatar';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/shared/ui/sheet';
import { ScrollProgress } from "@/components/shared/ui/scrollprogress";
export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
  const { openAuthModal } = useUIStore();
  const { user, isAuthenticated, logout } = useSession();
  const navigate = useNavigate();
  const { state: cartState, setIsOpen: setCartOpen } = useCartStore();

  const handleSignOut = async () => {
    try {
      await logout();
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <>
      <div className="text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] text-center font-medium py-2 bg-gradient-to-r from-[#829672] via-[#d8959b] to-[#829672]">
        <p className='text-white'>Exclusive Price Drop! Hurry, Offer Ends Soon!</p>
      </div>
      <nav className="sticky top-0 z-50 bg-[#f4f3f1] shadow-sm border-b border-black border-b-[0.5px]">
        <ScrollProgress className="top-[65px]" />
        <div className="max-w-[200vh] mx-auto  px-4 md:px-10 lg:px-16 xl:px-32 ">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src="/images/LOGOMATCH_opt.webp"
                  alt="MATCH Logo"
                  width={118}
                  height={32}
                  className="h-8 w-auto object-contain"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-5 font-jost sm:text-[14px] sm:text-black-500 xl:text-[15px]">
              <Link
                to="/"
                className=" font-jost text-black-500 hover:text-primary transition-colors tracking-[0.5px]"
              >
                INICIO
              </Link>
              <MegaMenu />
              <Link
                to="/nosotros"
                className=" font-jost text-black-500 hover:text-primary transition-colors tracking-[0.5px]"
              >
                NOSOTROS
              </Link>
              <Link
                to="/contacto"
                className=" font-jost text-black-500 hover:text-primary transition-colors tracking-[0.5px]"
              >
                CONTACTO
              </Link>
            </div>

            {/* Right side icons */}
            <div className="flex items-center gap-2">
              {/* Search Overlay Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsSearchOverlayOpen(true)}
                className="hidden lg:flex focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none"
                aria-label="Buscar"
              >
                <Search style={{ width: '20px', height: '20px', color: '#344c3d' }} />
              </Button>

              {/* Favorites Link */}
              <Button variant="ghost" size="icon" asChild className="focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none" >
                <Link to="/favoritos" aria-label="Ir a Favoritos">
                  <Heart style={{ width: '20px', height: '20px', color: '#344c3d' }} />
                </Link>
              </Button>

              <CartButton />

              {/* User Authentication / Profile */}
              {isAuthenticated && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-9 w-9 rounded-full hidden lg:flex items-center justify-center p-0 border-[#d8959b] border-[2px] bg-white focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none" aria-label="Opciones de usuario">
                      <CustomAvatar
                        src={user?.avatar_url}
                        fallbackText={user?.name || user?.first_name || (user?.email ? user.email.charAt(0).toUpperCase() : 'U')}
                        className="h-full w-full"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="font-jost">
                    <DropdownMenuItem
                      onClick={() => {
                        navigate('/perfil');
                        setIsOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <User className="mr-2 h-4 w-4" />
                      <span className="font-jost font-semibold mr-2 text-[12px]">Mi perfil</span>
                      <div className="ml-auto text-[12px]  text-muted-foreground font-jost">
                        {user?.email}
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/perfil?tab=orders" className="flex items-center  font-semibold font-jost text-[12px]">
                        <ShoppingBag className="mr-2 h-4 w-4" />
                        Mis Pedidos
                      </Link>
                    </DropdownMenuItem>
                    {(user?.isAdmin || user?.role === 'admin') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <a href={window.location.hostname === 'localhost' ? '/login?admin=true' : 'https://manage.matchactive.shop/login'} className="flex items-center">
                            <Settings className="mr-2 h-4 w-4" />
                            Panel Admin
                          </a>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-[12px] font-semibold font-jost">
                      <LogOut className="mr-2 h-4 w-4" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => openAuthModal('login')}
                  className="text-sm hidden lg:inline-flex font-jost rounded-full px-6 border-[#d8959b] text-[#d8959b] hover:bg-[#d8959b] hover:text-white transition-all shadow-sm"
                >
                  Iniciar Sesión
                </Button>
              )}

              {/* Mobile menu button (TRIGGER) */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Abrir menú de navegación">
                    <Menu className="h-6 w-6" style={{ width: '22px', height: '22px' }} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85%] sm:max-w-sm p-0 flex flex-col h-full border-none shadow-none bg-[#f8f8f8]">

                  {/* 1. Header: MATCH Branding */}
                  <div className="flex items-start justify-between px-4 py-8 pb-5">
                    <div className="flex flex-col">

                      <Link to="/" className="flex items-center">
                        <img
                          src="/images/LOGOMATCH_opt.webp"
                          alt="MATCH Logo"
                          width={118}
                          height={32}
                          className="h-8 w-auto object-contain"
                        />
                      </Link>
                      <span className="text-[12px] text-[#344c3d]/60  font-medium font-jost">Empower Your Potential, Define Your Style.</span>
                    </div>
                    <SheetClose className="  transition-transform active:scale-90">
                      <X className="h-7 w-7 text-black stroke-[1px]" />
                    </SheetClose>
                  </div>

                  {/* User Profile Positioned at the Top (Red Box in Image 1) */}
                  {isAuthenticated && user && (
                    <div className="px-4 bg-black/5 py-1 flex items-center gap-2 transition-all animate-in fade-in slide-in-from-top-2 duration-700">
                      <CustomAvatar
                        src={user?.avatar_url}
                        fallbackText={user?.name || user?.first_name || (user?.email ? user.email.charAt(0).toUpperCase() : 'U')}
                        className="h-14 w-14 border-[1.5px] border-[#d8959b] p-[2px] bg-white rounded-full object-cover shadow-sm"
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="font-jost text-[15px] font-semibold text-[#1a261e] leading-tight truncate uppercase">
                          {user.name || user.first_name || 'Usuario'}
                        </span>
                        <span className="text-[12px] text-slate-500 truncate tracking-tight font-jost">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* 2. Navigation Links */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="flex flex-col py-2">
                      <Link
                        to="/"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-4 text-sm font-jost text-black font-semibold hover:bg-black/5 transition-all tracking-[1.5px] border-b border-black/5 uppercase"
                      >
                        INICIO
                      </Link>
                      <Link
                        to="/tienda"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-4 text-sm font-jost text-black font-semibold hover:bg-black/5 transition-all tracking-[1.5px] border-b border-black/5 uppercase"
                      >
                        TIENDA
                      </Link>
                      <Link
                        to="/nosotros"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-4 text-sm font-jost text-black font-semibold hover:bg-black/5 transition-all tracking-[1.5px] border-b border-black/5 uppercase"
                      >
                        NOSOTROS
                      </Link>
                      <Link
                        to="/contacto"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-4 text-sm font-jost font-semibold text-black hover:bg-black/5 transition-all tracking-[1.5px] border-b border-black/5 uppercase"
                      >
                        CONTACTO
                      </Link>
                    </div>
                  </div>

                  {/* 3. Footer: User Info & Login */}
                  <div className="p-4 bg-[#efefef]">
                    {isAuthenticated && user ? (
                      <div className="grid gap-2 outline-none border-none">
                        <Button
                          variant="default"
                          className="w-full bg-[#1a261e] text-white rounded-none h-12 uppercase tracking-[2px] font-jost text-[13px] font-bold"
                          onClick={() => { navigate('/perfil'); setIsOpen(false); }}
                        >
                          Ir a mi Perfil
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full border-black/10 bg-white rounded-none h-12 uppercase tracking-[2px] font-jost text-[13px] font-bold text-red-500 hover:bg-red-50 hover:text-red-600 border"
                          onClick={handleSignOut}
                        >
                          Cerrar Sesión
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Button
                          onClick={() => { openAuthModal('login'); setIsOpen(false); }}
                          className="w-full bg-[#1a261e] text-white hover:bg-[#2a3d31] uppercase tracking-[2.5px] text-[11px] h-14 rounded-none font-bold shadow-none transition-all"
                        >
                          INICIAR SESIÓN
                        </Button>
                        <Button
                          onClick={() => { openAuthModal('register'); setIsOpen(false); }}
                          variant="outline"
                          className="w-full border-black/20 bg-transparent hover:bg-black/5 uppercase tracking-[2.5px] text-[11px] h-14 rounded-none font-bold transition-all"
                        >
                          CREAR CUENTA
                        </Button>
                      </div>
                    )}

                    {/* Bottom Icons */}
                    <div className="flex items-center justify-center gap-10 mt-10">
                      <Link to="#" className="text-black/40 hover:text-black transition-colors" aria-label="Language">
                        <Globe className="h-6 w-6 stroke-[1.5px]" />
                      </Link>
                      <Link to="/contacto" className="text-black/40 hover:text-black transition-colors" aria-label="Email">
                        <Mail className="h-6 w-6 stroke-[1.5px]" />
                      </Link>
                      <Link to="#" className="text-black/40 hover:text-black transition-colors" aria-label="Share">
                        <Share2 className="h-6 w-6 stroke-[1.5px]" />
                      </Link>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Shopping Cart Modal */}
      <ShoppingCart
        isOpen={cartState.isOpen}
        onOpenChange={setCartOpen}
      />

      {/* Search Overlay */}
      <SearchOverlay isOpen={isSearchOverlayOpen} onClose={() => setIsSearchOverlayOpen(false)} />

      {/* Note: The global AuthModal is now handled in App.tsx by useUIStore */}
    </>
  );
};
